import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Path, Tree, TreeNode, PermissionLookup } from '@s8l/client-tree-lib';

import { CmsService } from 'src/app/services/cms.service';

@Component({
  selector: 'app-path-selector',
  templateUrl: './path-selector.component.html',
  styleUrls: ['./path-selector.component.scss']
})
export class PathSelectorComponent implements OnInit {
  @Input() public path: Path;
  @Output() public pathChange = new EventEmitter<Path>();

  @Input() public editing = false;
  @Output() public editingChange = new EventEmitter<boolean>();

  @Input() public language = null;
  @Input() public readonly = false;
  @Input() public offset = 0;
  @Input() public length = 0;

  public name: string;
  public options: string[] = [];
  public currentIndex: number = null;
  public isLoadingChildren = false;

  private tree: Tree;

  public get segments(): string[] {
    return this.path.removeFirst().get();
  }

  public get canAdd() {
    if (this.length == 0) {
      return true;
    }
    return this.path.length <= this.length;
  }

  public get open() {
    return this.editing;
  }

  public set open(val) {
    this.editing = val;
    this.editingChange.emit(val);
  }

  constructor(private cms: CmsService) {}

  public ngOnInit(): void {
    this.cms.tree.asObservable().subscribe(tree => {
      this.tree = tree;
    });
  }

  public push(seg: string) {
    const i = this.currentIndex;

    this.open = false;
    this.currentIndex = null;

    const segs = this.path.get();

    const name = (seg || '').trim();
    if (name.length === 0) {
      segs.splice(i + 1, 1);
    } else {
      if (i + 1 >= segs.length) {
        segs.push(name);
      } else {
        segs[i + 1] = name;
      }
    }
    this.path = new Path(segs, this.path.language);
    this.pathChange.emit(this.path);
  }

  public add() {
    this.name = null;
    this.open = !this.open;
    this.path = this.path.addLast('');
    this.pathChange.emit(this.path);

    this.editIndex(this.path.length - 2);
  }

  public clear() {
    const segs = this.path.get();
    this.path = new Path([segs[0]], this.path.language);
    this.pathChange.emit(this.path);
  }

  public async editIndex(i: number) {
    if (this.readonly) {
      return;
    }

    if (this.currentIndex && this.path.get()[this.currentIndex + 1].length === 0) {
      // fixup (invalid) empty segment we were editing previously
      this.path = new Path(
        this.path.get().filter(s => s.length > 0),
        this.path.language
      );
    }

    this.name = this.path.get()[i + 1];
    this.open = true;
    this.currentIndex = i;
    this.options = [];

    if (this.tree) {
      const parent = new Path(this.path.get().slice(0, i + 1), this.path.language);
      const node = this.tree.get(parent);

      // Lazy-load children if they don't exist yet
      if (node && (!node.children || node.children.length === 0)) {
        await this.loadChildren(node);
      }

      if (node && node.children) {
        this.options = node.children.map(c => (this.language ? c.names[this.language] : c.name)).filter(n => !!n);
      }
    }
  }

  private async loadChildren(node: TreeNode): Promise<void> {
    if (this.isLoadingChildren) {
      return;
    }

    try {
      this.isLoadingChildren = true;

      // Fetch children for this specific node using its UUID
      const response = await this.cms.cms.treeGet({
        language: this.cms.language,
        parentUuid: node.uuid,
        depth: 1
      });

      // Update the node with the fetched children
      if (response.root && response.root.children) {
        const rawFetchedChildren = response.root.children || [];

        // Convert plain objects to actual TreeNode instances
        const fetchedChildren = rawFetchedChildren.map(rawChild => this.createTreeNode(rawChild));

        // Deserialize and process fetched children
        fetchedChildren.forEach(fetchedChild => {
          if (fetchedChild.permissions && typeof fetchedChild.permissions === 'object' && !('grants' in fetchedChild.permissions)) {
            fetchedChild.permissions = PermissionLookup.of(fetchedChild.permissions);
          }
          if (!fetchedChild.children) {
            fetchedChild.children = [];
          }

          // Find matching child in existing children by uuid
          const existingChild = node.children?.find(c => c.uuid === fetchedChild.uuid);
          if (existingChild) {
            // Update only the grandchildren
            existingChild.children = (fetchedChild.children || []).map(grandchild => this.createTreeNode(grandchild));
          } else {
            // Child didn't exist, add it
            if (!node.children) {
              node.children = [];
            }
            node.children.push(fetchedChild);
          }
        });
      }
    } catch (error) {
      console.error(`Failed to load children for node ${node.path}:`, error);
    } finally {
      this.isLoadingChildren = false;
    }
  }

  private createTreeNode(data: any): TreeNode {
    // Convert plain objects to actual TreeNode instances with all methods
    return new TreeNode({
      ...data,
      children: (data.children || []).map((child: any) => this.createTreeNode(child))
    });
  }
}
